/**
 * Listening Audio Service
 * Manages pre-recorded phrase audios for /listening stored in Cloudflare R2.
 * Uses checklist_listening_geral.json for mapping and validation, with fallback to standard TTS.
 */

const CHECKLIST_URL = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/listening-audio/checklist_listening_geral.json';
const R2_BASE = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/';

export interface ListeningChecklistItem {
  language: string;
  level: string;
  id: string;
  text: string;
  audio_path: string;
  audio_exists_locally?: boolean;
}

const LANG_TO_R2: Record<string, string> = {
  en: 'english',
  es: 'spanish',
  fr: 'french',
  de: 'german',
  it: 'italian',
  pt: 'portuguese',
  ru: 'russian',
  hu: 'hungarian',
  english: 'english',
  spanish: 'spanish',
  french: 'french',
  german: 'german',
  italian: 'italian',
  portuguese: 'portuguese',
  russian: 'russian',
  hungarian: 'hungarian',
  ingles: 'english',
  espanhol: 'spanish',
  frances: 'french',
  alemao: 'german',
  italiano: 'italian',
  portugues: 'portuguese',
  russo: 'russian',
  hungaro: 'hungarian'
};

let checklistLoadingPromise: Promise<boolean> | null = null;
let isChecklistLoaded = false;

// Lookups:
// byIdMap: `${langFolder}_${level.toUpperCase()}_${id}` -> audio_path
const byIdMap = new Map<string, string>();
// byTextMap: `${langFolder}_${level.toUpperCase()}_${normalizedText}` -> audio_path
const byTextMap = new Map<string, string>();

export const loadListeningChecklist = async (): Promise<boolean> => {
  if (isChecklistLoaded) return true;
  if (checklistLoadingPromise) return checklistLoadingPromise;

  checklistLoadingPromise = (async () => {
    try {
      const res = await fetch(CHECKLIST_URL);
      if (!res.ok) {
        console.warn('[listeningAudioService] Failed to fetch checklist:', res.status);
        return false;
      }
      const items: ListeningChecklistItem[] = await res.json();
      if (!Array.isArray(items)) return false;

      for (const item of items) {
        if (!item || !item.language || !item.level || !item.audio_path) continue;
        const lang = item.language.toLowerCase().trim();
        const lvl = item.level.toUpperCase().trim();

        if (item.id) {
          byIdMap.set(`${lang}_${lvl}_${item.id}`, item.audio_path);
          byIdMap.set(`${lang}_${item.id}`, item.audio_path);
        }

        if (item.text) {
          const cleanText = item.text.trim().toLowerCase();
          byTextMap.set(`${lang}_${lvl}_${cleanText}`, item.audio_path);
        }
      }

      isChecklistLoaded = true;
      return true;
    } catch (err) {
      console.warn('[listeningAudioService] Error loading listening checklist:', err);
      return false;
    } finally {
      checklistLoadingPromise = null;
    }
  })();

  return checklistLoadingPromise;
};

export interface ListeningAudioUrls {
  primaryUrl: string;
  fallbackUrl: string;
  audioPath: string;
}

export const getListeningAudioUrls = (
  langInput: string,
  levelInput: string,
  cardId?: string,
  cardText?: string
): ListeningAudioUrls | null => {
  const langKey = (langInput || 'en').toLowerCase().trim();
  const langFolder = LANG_TO_R2[langKey] || langKey;
  const level = (levelInput || 'A1').toUpperCase().trim();

  let audioPath: string | null = null;

  // 1. Try finding in loaded checklist by ID
  if (cardId) {
    audioPath = byIdMap.get(`${langFolder}_${level}_${cardId}`) || byIdMap.get(`${langFolder}_${cardId}`) || null;
  }

  // 2. Try finding in loaded checklist by text
  if (!audioPath && cardText) {
    const cleanText = cardText.trim().toLowerCase();
    audioPath = byTextMap.get(`${langFolder}_${level}_${cleanText}`) || null;
  }

  // 3. If checklist has not finished loading or cardId follows standard flashcards-store format
  if (!audioPath && cardId && /^[a-zA-Z0-9_]+$/.test(cardId) && !cardId.startsWith('palavra_') && !cardId.startsWith('custom_')) {
    audioPath = `listening-audio/${langFolder}/${level}/id-${cardId}.mp3`;
  }

  if (!audioPath) return null;

  // Remove leading slash if any
  const cleanPath = audioPath.replace(/^\/+/, '');

  return {
    audioPath: cleanPath,
    primaryUrl: `${R2_BASE}${cleanPath}`,
    fallbackUrl: `${R2_BASE}biblioteca-backup/${cleanPath}`
  };
};
